import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Dialog, DialogTitle, IconButton, Stack } from "@mui/material";
import {
  filterSubscriptionPlans,
  handleButtonText,
} from "../../../../../tenant/add-tenant/add-new-tenet-helper";
import {
  changeSubscriptionPlan,
  getSubscriptionPlans,
} from "../../../../../../api/tenets";
import { useSetLoading } from "../../../../../../common/hooks/appearance";
import { routes } from "../../../../../../constants/routes";
import {
  AlertComponent,
  BreadcrumbComponent,
  SubscriptionCard,
} from "../../../../../../common/components";
import { bgColors } from "../../../../../tenant/add-tenant/subscriptions-plans/subscription-plans";
import CreateCustomPlan from "../../../../../tenant/crm-plans/crm-custom-plans/create-custom-plan/create-custom-plan";
import CustomPlanCard from "../../../../../../common/components/subscription-card/custom-plan-card";
import { initialValues } from "../../../../../tenant/crm-plans/crm-plans-utils";
import { ALERT_TYPE } from "../../../../../../utils/constants";
import { AlertType } from "../../../../../../utils/util-types";

const tabs = ["Billed Monthly", "Billed Annually", "No Cost"];

const ChangePlan = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = location;
  const { activePlanDetails, tenantData } = state || {};
  console.log("tenantData", tenantData);
  const [activePlan, setActivePlan] = useState<any>(
    activePlanDetails
      ? {
          ...activePlanDetails,
          id: activePlanDetails?.subscription_plan_id,
          plan_type: activePlanDetails?.frequency,
        }
      : {}
  );
  const [pendingPlan, setPendingPlan] = useState<any>(
    tenantData?.pending_subscription_plan
      ? {
          ...tenantData?.pending_subscription_plan,
          id: tenantData?.pending_subscription_plan?.subscription_plan_id,
        }
      : {}
  );
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertType, setAlertType] = useState<AlertType>(ALERT_TYPE.SUCCESS);
  const [createCustomPlanOpen, setCreateCustomPlanOpen] = useState(false);
  const [planInitialValues] = useState<typeof initialValues>(initialValues);

  const handleOnClick = (item: any) => {
    setSelectedPlan(item);
  };

  const updateSubscriptionPlan = async () => {
    try {
      const planType = handleButtonText(selectedPlan, activePlan);
      setIsLoading(true);
      const payload = {
        subscriptionPlanId: selectedPlan.id,
        changeType: planType === "Choose plan" ? "choosePlan" : planType,
        planCategory: selectedPlan?.plan_category
          ?.toLowerCase()
          ?.split("_")
          ?.join(" "),
        planType: selectedPlan?.plan_type,
      };
      if (selectedPlan?.plan_category !== "TRIAL") {
        payload.planType = selectedPlan?.plan_type?.toLowerCase();
      }
      const resp = await changeSubscriptionPlan(tenantData?.id, payload);
      if (
        resp?.data?.data?.message ===
        "Subscription plan has been changed successfully"
      ) {
        setActivePlan(selectedPlan);
      } else {
        setPendingPlan(selectedPlan);
      }
      setSelectedPlan(null);

      setAlertMessage(
        resp?.data?.data?.message || "Upgrade subscription plan email sent."
      );
      setAlertType(ALERT_TYPE.SUCCESS);
      setAlertOpen(true);
      setIsLoading(false);
    } catch (err: any) {
      const errMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Something went wrong!";
      setAlertMessage(errMessage);
      setAlertType("error");
      setAlertOpen(true);
      setIsLoading(false);
    }
  };

  const getSubscriptionValues = async () => {
    try {
      setIsLoading(true);
      const response = await getSubscriptionPlans();
      const {
        monthly,
        annually,
        noCost,
        activeTab,
        customMonthly,
        customAnnual,
      } = filterSubscriptionPlans(response?.data?.data, {
        ...activePlanDetails,
        id: activePlanDetails?.subscription_plan_id,
        plan_type: activePlanDetails?.frequency,
      });
      setActiveTab(activeTab);
      setIsLoading(false);

      setSubscriptionData({
        monthly: monthly,
        annually: annually,
        noCost: noCost,
        customMonthly: customMonthly,
        customAnnual: customAnnual,
      });
    } catch (e: any) {
      setIsLoading(false);
      setAlertMessage(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          "Some thing went wrong!"
      );
      setAlertType("error");
      setAlertOpen(true);
      console.error(e);
    }
  };

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  useEffect(() => {
    getSubscriptionValues();
  }, []);

  const breadcrumbLink = [
    {
      label: "Orders",
      link: "#",
    },
    {
      label: "Order Details",
      handleClick: () => {
        navigate(-1);
      },
    },
    {
      label: "Change Plan",
      handleClick: () => {},
    },
  ];
  return (
    <Box className="change-plan-wrapper">
      <Box className="change-plan-header">
        <BreadcrumbComponent
          breadcrumbLink={breadcrumbLink}
          showBackArrow
          handleBackArrowClick={() => navigate(routes.ORDERSLIST)}
        />
      </Box>
      <Box className="change-plan-body">
        <Box>
          <Box className="tabs-wrapper">
            <Box className="tabs-container" gap={3}>
              {tabs?.map((tab: string, index: number) => (
                <span
                  key={index}
                  className={index === activeTab ? "active" : ""}
                  onClick={() => setActiveTab(index)}
                >
                  {tab}
                </span>
              ))}
            </Box>
          </Box>
          {activeTab === 0 && (
            <Stack
              spacing={{ xs: 1, sm: 2, md: 4 }}
              direction="row"
              useFlexGap
              sx={{ flexWrap: "wrap", justifyContent: "center" }}
            >
              {subscriptionData?.monthly?.map((item: any, index: number) => (
                <SubscriptionCard
                  key={index}
                  item={item}
                  onClick={handleOnClick}
                  selectedItem={selectedPlan || ""}
                  bgColor={bgColors[index % bgColors.length]}
                  isShowActiveBadge={
                    item?.id === activePlan?.id &&
                    activePlan?.plan_type == item?.plan_type
                  }
                  buttonText={handleButtonText(item, activePlan)}
                  isPending={
                    pendingPlan &&
                    pendingPlan?.id === item?.id &&
                    pendingPlan?.plan_type == item?.plan_type
                  }
                  activePlan={activePlan}
                />
              ))}
              {subscriptionData?.customMonthly?.length > 0 && (
                <CustomPlanCard
                  plans={subscriptionData.customMonthly}
                  activePlan={activePlan}
                  selectedPlan={selectedPlan}
                  onChoosePlan={(plan: any) => {
                    setSelectedPlan(plan);
                  }}
                  onCreate={() => setCreateCustomPlanOpen(true)}
                  pendingPlan={pendingPlan}
                />
              )}
            </Stack>
          )}
          {activeTab === 1 && (
            <Stack
              spacing={{ xs: 1, sm: 2, md: 4 }}
              direction="row"
              useFlexGap
              sx={{ flexWrap: "wrap", justifyContent: "center" }}
            >
              {subscriptionData?.annually?.map((item: any, index: number) => (
                <SubscriptionCard
                  key={index}
                  item={item}
                  onClick={handleOnClick}
                  selectedItem={selectedPlan || ""}
                  bgColor={bgColors[index % bgColors.length]}
                  isShowActiveBadge={
                    item?.id === activePlan?.id &&
                    activePlan?.plan_type == item?.plan_type
                  }
                  buttonText={handleButtonText(item, activePlan)}
                  isPending={
                    pendingPlan &&
                    pendingPlan?.id === item?.id &&
                    pendingPlan?.plan_type == item?.plan_type
                  }
                  activePlan={activePlan}
                />
              ))}
              {subscriptionData?.customAnnual?.length > 0 && (
                <CustomPlanCard
                  plans={subscriptionData.customAnnual}
                  activePlan={activePlan}
                  selectedPlan={selectedPlan}
                  onChoosePlan={(plan: any) => {
                    setSelectedPlan(plan);
                  }}
                  onCreate={() => setCreateCustomPlanOpen(true)}
                  pendingPlan={pendingPlan}
                />
              )}
            </Stack>
          )}
          {activeTab === 2 && (
            <Stack
              spacing={{ xs: 1, sm: 2, md: 4 }}
              direction="row"
              useFlexGap
              sx={{ flexWrap: "wrap", justifyContent: "center" }}
            >
              {subscriptionData?.noCost?.length ? (
                subscriptionData?.noCost?.map((item: any, index: number) => (
                  <SubscriptionCard
                    key={index}
                    item={item}
                    onClick={handleOnClick}
                    selectedItem={selectedPlan || ""}
                    bgColor={bgColors[index % bgColors.length]}
                    isShowActiveBadge={item?.id === activePlan?.id}
                    buttonText={handleButtonText(item, activePlan)}
                    isPending={pendingPlan && pendingPlan?.id === item?.id}
                    activePlan={activePlan}
                  />
                ))
              ) : (
                <Box>No Plans Available</Box>
              )}
            </Stack>
          )}
        </Box>
        <Box className="save-user-button-wrapper">
          <Box sx={{ display: "flex", marginRight: "15px", marginTop: "20px" }}>
            <button
              type="button"
              className="cancel-button"
              disabled={!selectedPlan}
              onClick={() => {
                setSelectedPlan(null);
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="save-button next-button-wrapper"
              disabled={!selectedPlan}
              onClick={() => {
                if (selectedPlan) updateSubscriptionPlan();
              }}
            >
              Save
            </button>
          </Box>
        </Box>
      </Box>

      {alertOpen && (
        <AlertComponent
          message={alertMessage}
          type={alertType}
          open={alertOpen}
          onClose={() => {
            setAlertOpen(false);
          }}
        />
      )}
      <Dialog
        open={createCustomPlanOpen}
        onClose={() => setCreateCustomPlanOpen(false)}
        fullWidth
        maxWidth="lg"
        slotProps={{
          paper: {
            sx: {
              backgroundColor: "#f1f1f1",
            },
          },
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>Create Custom Plan</span>

            <IconButton
              aria-label="close"
              onClick={() => setCreateCustomPlanOpen(false)}
              sx={{
                color: "#555",
                "&:hover": { color: "black" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <CreateCustomPlan
          initialValues={planInitialValues}
          setIsLoading={setIsLoading}
          setAlertOpen={setAlertOpen}
          setAlertMessage={setAlertMessage}
          setAlertType={setAlertType}
          setCreateCustomPlanOpen={setCreateCustomPlanOpen}
          fetchPlans={async () => {
            await getSubscriptionValues?.();
          }}
        />
      </Dialog>
    </Box>
  );
};

export default ChangePlan;
